import { useState, useEffect, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getRoleLabel } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

export const FORM_COMPANY_INVITE_ID = 'form-company-invite';

const initialInvite = { firstName: '', lastName: '', email: '', role: '' };

const ADMIN_ROLE = 'admin';
const OWNER_ROLE = 'owner';
const HIDDEN_ROLES = ['user', 'guest'];

export default function FormCompanyInvite({ onSubmit, roles = [], hasOwner = false, isSuperAdmin = false }) {
    const { t } = useTranslation();
    const [invites, setInvites] = useState([{ ...initialInvite }]);

    const availableRoles = useMemo(
        () =>
            roles.filter((r) => {
                const name = r.name?.toLowerCase();
                if (HIDDEN_ROLES.includes(name)) return false;
                if (name === ADMIN_ROLE && !isSuperAdmin) return false;
                if (name === OWNER_ROLE && hasOwner) return false;
                return true;
            }),
        [roles, isSuperAdmin, hasOwner],
    );

    const handleChange = (index, field, value) => {
        setInvites((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    useEffect(() => {
        if (availableRoles.length > 0) {
            const defaultRole = availableRoles[1]?.uniq_id || availableRoles[0]?.uniq_id;
            setInvites((prev) => prev.map((inv) => (inv.role ? inv : { ...inv, role: defaultRole })));
        }
    }, [availableRoles]);

    const addInvite = () =>
        setInvites((prev) => [
            ...prev,
            {
                ...initialInvite,
                role: availableRoles.length > 0 ? availableRoles[1]?.uniq_id || availableRoles[0]?.uniq_id : '',
            },
        ]);

    const removeInvite = (index) => {
        setInvites((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const cleaned = invites.filter((i) => i.email.trim());
        if (cleaned.length === 0) return;
        onSubmit(cleaned);
    };

    return (
        <form id={FORM_COMPANY_INVITE_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
            <div className="space-y-5 mb-5">
                {invites.map((row, index) => (
                    <div key={index} className="bg-muted rounded-xl p-4 relative border border-border">
                        {index > 0 && (
                            <Button
                                type="button"
                                variant="plain"
                                color="secondary"
                                size="auto"
                                onClick={() => removeInvite(index)}
                                className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-secondary hover:bg-secondary/80 p-0 z-10"
                            >
                                <X className="h-4 w-4 text-foreground" />
                            </Button>
                        )}
                        <div className={`space-y-2`}>
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    placeholder={t('account:prenom2')}
                                    value={row.firstName}
                                    onChange={(e) => handleChange(index, 'firstName', e.target.value)}
                                    className="flex-1 min-w-0 bg-card"
                                    required
                                />
                                <Input
                                    type="text"
                                    placeholder={t('account:nom')}
                                    value={row.lastName}
                                    onChange={(e) => handleChange(index, 'lastName', e.target.value)}
                                    className="flex-1 min-w-0 bg-card"
                                    required
                                />
                            </div>
                            <Input
                                type="email"
                                placeholder={t('account:email2')}
                                value={row.email}
                                onChange={(e) => handleChange(index, 'email', e.target.value)}
                                className="bg-card"
                                required
                            />
                            <Select value={row.role} onValueChange={(value) => handleChange(index, 'role', value)}>
                                <SelectTrigger className="bg-card">
                                    <SelectValue placeholder={t('account:role2')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRoles.map((role) => (
                                        <SelectItem key={role.uniq_id} value={role.uniq_id}>
                                            {getRoleLabel(role.name || role.uniq_id)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                ))}
            </div>

            <Button
                type="button"
                variant="plain"
                color="neutral"
                size="auto"
                onClick={addInvite}
                className="flex items-center gap-2 mb-6 hover:opacity-70 hover:bg-transparent p-0 h-auto"
            >
                <div className="bg-foreground-dark rounded-md h-6 w-6 flex items-center justify-center">
                    <Plus className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
                </div>
                <span className="text-[14px] text-foreground">{t('account:ajouterUnCollaborateur')}</span>
            </Button>
        </form>
    );
}
